import { useId, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Xmark } from '@peacecover/icon';
import OriginalEpisodesLoadingOverlay from './OriginalEpisodesLoadingOverlay';
import { useGetSceneProducts } from '../../../domains/scene/ui/hooks/useGetSceneProducts';
import { useGetUserInfo } from '../../../domains/user/ui/hooks/useGetUserInfo';
import { openNewTab } from '../../../utils/UIUtils';
import AnimationStarBeam from './AnimationStarBeam';
import { ROUTES } from '../../../constants/routes';
import type { components } from '../../../types/schema';

type SceneProduct = components['schemas']['ApiV1SceneProduct'];
type SceneDetail = components['schemas']['ApiV1Scene'];
type CastRelationship = components['schemas']['ApiV1CastRelationship'];

interface SceneOriginalProductListProps {
  sceneId: string;
  castRelationshipId: string;
  sceneDetail: SceneDetail | null;
  isFullAccessPurchased: boolean;
  castRelationship: CastRelationship | null;
}

const SceneOriginalProductList = ({
  sceneId,
  castRelationshipId,
  sceneDetail,
  isFullAccessPurchased,
  castRelationship
}: SceneOriginalProductListProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);
  const { data: sceneProducts } = useGetSceneProducts({
    category: 'original_full',
    scene_id: sceneId,
    cast_relationship_id: castRelationshipId
  });
  const { data: user } = useGetUserInfo();

  const handleBuyProduct = (product: SceneProduct) => {
    if (isFullAccessPurchased) {
      if (sceneDetail?.status === 'completed') {
        const baseParams = new URLSearchParams();
        if (sceneDetail?.locator?.sceneEpisodeId)
          baseParams.set('episodeId', sceneDetail.locator.sceneEpisodeId);
        if (sceneDetail?.locator?.paragraphIndex != null)
          baseParams.set('cursor', String(sceneDetail.locator.paragraphIndex));
        if (sceneDetail?.locator?.charOffset != null)
          baseParams.set('charOffset', String(sceneDetail.locator.charOffset));

        const queryString = baseParams.toString();
        navigate(
          `${ROUTES.SCENE.ORIGINAL_EPISODES.GO(sceneId)}${queryString ? `?${queryString}` : ''}`
        );
      } else {
        setShowLoadingOverlay(true);
      }
      return;
    }
    const amount = Number(product?.amount);
    const baseParams = new URLSearchParams({
      amount: String(amount),
      productName: product?.name,
      productId: product?.sceneProductId,
      productType: 'scene_product',
      sceneId: sceneId
    });
    if (user?.identified) {
      openNewTab(`/payment/inicis?${baseParams.toString()}`);
    } else {
      openNewTab(`/identity-verification/intro?${baseParams.toString()}`);
    }
  };

  const type =
    sceneDetail?.contentType === 'SceneThread'
      ? t('ScenePreviewDetail.thread')
      : t('ScenePreviewDetail.chat');

  const adaptedProduct = sceneProducts && sceneProducts.length > 0 ? sceneProducts[0] : null;

  return (
    <div className="relative h-[543px] mt-[54px]">
      <div
        className="absolute inset-0 bg-[linear-gradient(180deg,#000_0%,rgba(0,0,0,0.00)_100%),linear-gradient(136deg,#15CCCB_1.21%,#4987FF_50.69%,#8B1CFA_101.7%)] opacity-50"
        aria-hidden
      />
      <div className="z-1 flex flex-col items-center w-full relative">
        <div className="w-[250px] h-[137px] mb-[-15px] relative">
          <AnimationStarBeam animate={false} />
          <Star className="absolute bottom-[1px] left-[1px]" brightness="high" />
          <Star className="absolute bottom-[21px] left-[24px]" brightness="medium" />
          <Star className="absolute bottom-[0px] left-[22px]" brightness="low" />
          <Star className="absolute bottom-[0px] right-[1px]" brightness="high" />
          <Star className="absolute bottom-[21px] right-[24px]" brightness="medium" />
          <Star className="absolute bottom-[1px] right-[22px]" brightness="low" />
        </div>
        <div className="flex flex-col items-center justify-center gap-[6px]">
          <p className="text-[18px] font-bold leading-[21px] text-pure-white">
            {t('ScenePreviewDetail.openAllPreview')}
          </p>
          <p className="text-[13px] leading-[16px] text-pure-white text-center">
            {t('ScenePreviewDetail.originalPreviewDescription', {
              type
            })}
          </p>
        </div>
        <div className="mt-[42px] px-[24px] flex flex-col items-center gap-[10px] w-full">
          <OriginalProductRow
            product={null}
            isFullAccessPurchased={isFullAccessPurchased}
            onBuyProduct={handleBuyProduct}
          />
          <AdaptedProductRow
            product={adaptedProduct}
            castRelationship={castRelationship}
            isFullAccessPurchased={isFullAccessPurchased}
            onBuyProduct={handleBuyProduct}
          />
        </div>
      </div>
      {showLoadingOverlay && (
        <OriginalEpisodesLoadingOverlay onClose={() => setShowLoadingOverlay(false)} />
      )}
    </div>
  );
};

export default SceneOriginalProductList;

const OriginalProductRow = ({
  product,
  isFullAccessPurchased,
  onBuyProduct
}: {
  product: SceneProduct | null;
  isFullAccessPurchased: boolean;
  onBuyProduct: (product: SceneProduct) => void;
}) => {
  const { t } = useTranslation();
  return (
    <div className="p-[18px] rounded-[16px] bg-white-opacity-50 w-full flex items-center justify-between relative overflow-hidden">
      <div className="bg-[linear-gradient(125deg,#5D3EFA_7.9%,#45DCAE_102.96%)] absolute top-[10px] left-[-24px] -rotate-45 text-[11px] font-semibold leading-[13px] text-pure-white flex items-center justify-center w-[84px] h-[19px]">
        Original
      </div>
      <div className="flex items-center gap-[11px]">
        <img
          src={product?.adaptedFiction?.pictureUrl ?? '/assets/scene/scene_empty_thumbnail.png'}
          alt={product?.adaptedFiction?.title ?? 'empty thumbnail'}
          className="w-[46px] h-[73px] rounded-[5px] border border-pure-black"
        />
        <div className="flex flex-col items-start gap-[6px]">
          {product ? (
            <>
              <p className="text-[17px] font-semibold leading-[20px] text-[#FFF] max-w-[134px] truncate">
                {product?.adaptedFiction?.title}
              </p>
              <p className="text-[12px] font-medium leading-[14px] text-[rgba(255,255,255,0.80)]">
                {product?.adaptedFiction?.author} ·{' '}
                {Number(product?.adaptedFiction?.wordCount).toLocaleString()}자
              </p>
              <p className="text-[16px] font-semibold leading-[19px] text-[#FFF]">
                {Number(product?.amount).toLocaleString()}원
              </p>
            </>
          ) : (
            <p className="text-[17px] font-semibold leading-[20px] text-[#FFF] whitespace-pre-line">
              {t('ScenePreviewDetail.emptyProductDescription')}
            </p>
          )}
        </div>
      </div>
      {product && (
        <button
          type="button"
          className="px-[12px] py-[8px] rounded-[32px] bg-[#FFF] text-[#444] font-semibold text-[14px] leading-[17px] border border-separator-light-1"
          onClick={() => onBuyProduct(product)}
        >
          {isFullAccessPurchased ? t('ScenePreviewDetail.watch') : t('ScenePreviewDetail.purchase')}
        </button>
      )}
    </div>
  );
};

const AdaptedProductRow = ({
  product,
  castRelationship,
  isFullAccessPurchased,
  onBuyProduct
}: {
  product: SceneProduct | null;
  castRelationship: CastRelationship | null;
  isFullAccessPurchased: boolean;
  onBuyProduct: (product: SceneProduct) => void;
}) => {
  const { t } = useTranslation();
  return (
    <div className="p-[18px] rounded-[16px] bg-white-opacity-50 w-full flex items-center justify-between relative overflow-hidden">
      <div className="flex items-center gap-[11px]">
        <img
          src={product?.adaptedFiction?.pictureUrl ?? '/assets/scene/scene_empty_thumbnail.png'}
          alt={product?.adaptedFiction?.title ?? 'empty thumbnail'}
          className="w-[46px] h-[73px] rounded-[5px] border border-pure-black"
        />
        <div className="flex flex-col items-start gap-[6px]">
          {product ? (
            <p className="text-[17px] font-semibold leading-[20px] text-[#FFF] max-w-[134px] truncate">
              {product?.adaptedFiction?.title}
            </p>
          ) : (
            <p className="text-[17px] font-semibold leading-[20px] text-[#FFF] whitespace-pre-line">
              {t('ScenePreviewDetail.emptyProductDescription')}
            </p>
          )}
          {castRelationship && <CastRelationshipBadge castRelationship={castRelationship} />}
          {product && (
            <p className="text-[16px] font-semibold leading-[19px] text-[#FFF]">
              {Number(product?.amount).toLocaleString()}원
            </p>
          )}
        </div>
      </div>
      {product && (
        <button
          type="button"
          className="px-[12px] py-[8px] rounded-[32px] bg-[#FFF] text-[#444] font-semibold text-[14px] leading-[17px] border border-separator-light-1"
          onClick={() => onBuyProduct(product)}
        >
          {isFullAccessPurchased ? t('ScenePreviewDetail.watch') : t('ScenePreviewDetail.purchase')}
        </button>
      )}
    </div>
  );
};

const CastRelationshipBadge = ({ castRelationship }: { castRelationship: CastRelationship }) => {
  return (
    <div className="px-[4px] py-[3px] bg-[rgba(0,0,0,0.2)] rounded-[4px] flex items-center gap-[5px] text-white text-[12px] leading-[14px] font-medium">
      <span>{castRelationship.personA?.name}</span>
      <Xmark size={12} />
      <span>{castRelationship.personB?.name}</span>
    </div>
  );
};

const BRIGHTNESS_CONFIG = {
  high: {
    fillOpacity: 0.85,
    glowOpacity: 0.5,
    orbOpacity: 0.5,
    orbSize: 16
  },
  medium: {
    fillOpacity: 0.55,
    glowOpacity: 0.35,
    orbOpacity: 0.35,
    orbSize: 13
  },
  low: {
    fillOpacity: 0.28,
    glowOpacity: 0.18,
    orbOpacity: 0.2,
    orbSize: 13
  }
} as const;

const Star = ({
  className,
  brightness = 'medium'
}: {
  className?: string;
  brightness?: 'high' | 'medium' | 'low';
}) => {
  const { fillOpacity, glowOpacity, orbOpacity, orbSize } = BRIGHTNESS_CONFIG[brightness];
  const id = useId().replace(/:/g, '');

  return (
    <span className={className}>
      <span
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
        style={{
          width: `${orbSize}px`,
          height: `${orbSize}px`,
          borderRadius: `${orbSize / 2}px`,
          background:
            'radial-gradient(50% 50% at 50% 50%, #FFF 0%, rgba(255, 255, 255, 0.00) 100%)',
          filter: 'blur(3.6px)',
          opacity: orbOpacity
        }}
        aria-hidden
      />
      <svg
        width="21"
        height="27"
        viewBox="0 0 21 27"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-label="star"
        role="img"
        className="relative z-[1]"
      >
        <g filter={`url(#filter0_${id})`}>
          <path
            d="M10.4302 5.09998L11.3851 9.78443L14.7603 8.59998L12.34 12.1L14.7603 15.6L11.3851 14.4155L10.4302 19.1L9.47526 14.4155L6.10005 15.6L8.52035 12.1L6.10005 8.59998L9.47526 9.78443L10.4302 5.09998Z"
            fill={`url(#paint0_${id})`}
            fillOpacity={fillOpacity}
            shapeRendering="crispEdges"
          />
        </g>
        <g opacity={glowOpacity} filter={`url(#filter1_${id})`}>
          <path
            d="M10.6028 7.92163L11.081 10.7537L12.7715 10.0376L11.5593 12.1537L12.7715 14.2697L11.081 13.5536L10.6028 16.3857L10.1245 13.5536L8.43413 14.2697L9.64629 12.1537L8.43413 10.0376L10.1245 10.7537L10.6028 7.92163Z"
            fill={`url(#paint1_${id})`}
          />
        </g>
        <defs>
          <filter
            id={`filter0_${id}`}
            x="9.77516e-05"
            y="-2.43187e-05"
            width="20.8602"
            height="26.2"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feColorMatrix
              in="SourceAlpha"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha"
            />
            <feOffset dy="1" />
            <feGaussianBlur stdDeviation="2" />
            <feComposite in2="hardAlpha" operator="out" />
            <feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0 0" />
            <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow" />
            <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow" result="shape" />
            <feGaussianBlur stdDeviation="0.6" result="effect2_foregroundBlur" />
          </filter>
          <filter
            id={`filter1_${id}`}
            x="7.93408"
            y="7.42163"
            width="5.3374"
            height="9.46411"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
            <feGaussianBlur stdDeviation="0.15" result="effect1_foregroundBlur" />
          </filter>
          <radialGradient
            id={`paint0_${id}`}
            cx="0"
            cy="0"
            r="1"
            gradientUnits="userSpaceOnUse"
            gradientTransform="translate(10.4302 12.1) rotate(90) scale(7 5)"
          >
            <stop stopColor="white" />
            <stop offset="1" stopColor="white" stopOpacity="0" />
          </radialGradient>
          <radialGradient
            id={`paint1_${id}`}
            cx="0"
            cy="0"
            r="1"
            gradientUnits="userSpaceOnUse"
            gradientTransform="translate(10.6028 12.1537) rotate(90) scale(4.23203 2.50416)"
          >
            <stop stopColor="white" />
            <stop offset="1" stopColor="white" stopOpacity="0" />
          </radialGradient>
        </defs>
      </svg>
    </span>
  );
};